import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Inventory as ProductsIcon,
  Edit as EditIcon,
  Add as AddIcon,
  AttachMoney as PriceIcon,
  Delete as DeleteIcon,
  Layers as BundleIcon,
} from '@mui/icons-material';
import * as adminApi from '../../api/admin';
import type { PaymentProduct, ProductType, CreateProductInput, UpdateProductInput, CreatePriceInput, BundleItemInput } from '../../api/admin';
import { useDocumentTitle } from '../../hooks/core';

export function ProductsPage() {
  useDocumentTitle('Products');

  const [products, setProducts] = useState<PaymentProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Create product dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newProduct, setNewProduct] = useState<CreateProductInput>({
    lookupKey: '',
    name: '',
    description: '',
    type: 'credit_pack',
    metadata: {},
    displayOrder: 0,
  });
  // Initial price for new product (optional)
  const [newProductPrice, setNewProductPrice] = useState<{
    amount: number;
    currency: string;
    interval?: 'month' | 'year';
    intervalCount?: number;
  }>({
    amount: 0,
    currency: 'usd',
  });

  // Edit product dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editProduct, setEditProduct] = useState<PaymentProduct | null>(null);
  const [editInput, setEditInput] = useState<UpdateProductInput>({});

  // Add price dialog
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [priceProduct, setPriceProduct] = useState<PaymentProduct | null>(null);
  const [newPrice, setNewPrice] = useState<Omit<CreatePriceInput, 'productId'>>({
    amount: 0,
    currency: 'usd',
  });

  // Bundle items for create
  const [selectedBundleItems, setSelectedBundleItems] = useState<BundleItemInput[]>([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await adminApi.listProducts();
      setProducts(data);
      setError(null);
    } catch (err) {
      setError('Failed to load products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    try {
      setCreateLoading(true);
      setCreateError(null);
      const input: CreateProductInput = {
        ...newProduct,
        bundleItems: newProduct.type === 'bundle' ? selectedBundleItems : undefined,
      };
      const createdProduct = await adminApi.createProduct(input);

      // If a price amount is provided, create the price too
      if (newProductPrice.amount > 0) {
        await adminApi.createPrice({
          productId: createdProduct.id,
          amount: newProductPrice.amount,
          currency: newProductPrice.currency,
          interval: newProductPrice.interval,
          intervalCount: newProductPrice.intervalCount,
        });
        setSuccess('Product and price created successfully');
      } else {
        setSuccess('Product created successfully');
      }

      setCreateDialogOpen(false);
      resetCreateForm();
      fetchProducts();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create product';
      setCreateError(errorMessage);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateProduct = async () => {
    if (!editProduct) return;
    try {
      setEditLoading(true);
      setEditError(null);
      await adminApi.updateProduct(editProduct.id, editInput);
      setSuccess('Product updated successfully');
      setEditDialogOpen(false);
      fetchProducts();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update product';
      setEditError(errorMessage);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteProduct = async (product: PaymentProduct) => {
    if (!confirm(`Are you sure you want to deactivate "${product.name}"?`)) return;
    try {
      await adminApi.deleteProduct(product.id);
      setSuccess('Product deactivated successfully');
      fetchProducts();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to deactivate product';
      setError(errorMessage);
    }
  };

  const handleCreatePrice = async () => {
    if (!priceProduct) return;
    try {
      setPriceLoading(true);
      setPriceError(null);
      await adminApi.createPrice({
        productId: priceProduct.id,
        ...newPrice,
      });
      setSuccess('Price created successfully');
      setPriceDialogOpen(false);
      setNewPrice({ amount: 0, currency: 'usd' });
      fetchProducts();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create price';
      setPriceError(errorMessage);
    } finally {
      setPriceLoading(false);
    }
  };

  const handleDeactivatePrice = async (priceId: string) => {
    if (!confirm('Are you sure you want to deactivate this price?')) return;
    try {
      await adminApi.deactivatePrice(priceId);
      setSuccess('Price deactivated successfully');
      fetchProducts();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to deactivate price';
      setError(errorMessage);
    }
  };

  const resetCreateForm = () => {
    setNewProduct({
      lookupKey: '',
      name: '',
      description: '',
      type: 'credit_pack',
      metadata: {},
      displayOrder: 0,
    });
    setNewProductPrice({
      amount: 0,
      currency: 'usd',
    });
    setSelectedBundleItems([]);
    setCreateError(null);
  };

  const openEditDialog = (product: PaymentProduct) => {
    setEditProduct(product);
    setEditInput({
      name: product.name,
      description: product.description,
      metadata: product.metadata,
      active: product.active,
      displayOrder: product.displayOrder,
    });
    setEditError(null);
    setEditDialogOpen(true);
  };

  const openPriceDialog = (product: PaymentProduct) => {
    setPriceProduct(product);
    setPriceError(null);
    setNewPrice({ amount: 0, currency: 'usd' });
    setPriceDialogOpen(true);
  };

  const formatPrice = (amount: number): string => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const getProductTypeColor = (type: ProductType) => {
    switch (type) {
      case 'credit_pack':
        return 'primary';
      case 'bundle':
        return 'warning';
      case 'subscription':
        return 'secondary';
      case 'feature':
        return 'info';
      default:
        return 'default';
    }
  };

  const getProductTypeLabel = (type: ProductType): string => {
    switch (type) {
      case 'credit_pack':
        return 'Credit Pack';
      case 'bundle':
        return 'Bundle';
      case 'subscription':
        return 'Subscription';
      case 'feature':
        return 'Feature';
      default:
        return type;
    }
  };

  // Get available products for bundle (non-bundles only)
  const availableForBundle = products.filter(p => p.type !== 'bundle' && p.active);

  const addBundleItem = (productId: string) => {
    if (selectedBundleItems.find(item => item.productId === productId)) return;
    setSelectedBundleItems([...selectedBundleItems, { productId, quantity: 1 }]);
  };

  const removeBundleItem = (productId: string) => {
    setSelectedBundleItems(selectedBundleItems.filter(item => item.productId !== productId));
  };

  const updateBundleItemQuantity = (productId: string, quantity: number) => {
    setSelectedBundleItems(selectedBundleItems.map(item =>
      item.productId === productId ? { ...item, quantity } : item
    ));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ProductsIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4">Products</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Product
        </Button>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage credit packs, bundles, and other purchasable products. All products are prefixed with "squareup_" for clear identification.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell>Lookup Key</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No products found. Create a product to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => {
                  const activePrice = product.prices?.find(p => p.active);
                  return (
                    <TableRow key={product.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium">{product.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {product.description}
                        </Typography>
                        {product.type === 'bundle' && product.bundleItems && product.bundleItems.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              size="small"
                              icon={<BundleIcon />}
                              label={`${product.bundleItems.length} items`}
                              variant="outlined"
                            />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                          {product.lookupKey}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getProductTypeLabel(product.type)}
                          size="small"
                          color={getProductTypeColor(product.type) as 'primary' | 'secondary' | 'warning' | 'info' | 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {activePrice ? (
                          <>
                            <Typography variant="body2">
                              {formatPrice(activePrice.amount)}
                            </Typography>
                            {activePrice.interval && (
                              <Typography variant="caption" color="text.secondary">
                                / {activePrice.interval}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No price
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {product.metadata.boardCredits || '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={product.active ? 'Active' : 'Inactive'}
                          size="small"
                          color={product.active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Add Price">
                          <IconButton size="small" onClick={() => openPriceDialog(product)}>
                            <PriceIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditDialog(product)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {product.active && (
                          <Tooltip title="Deactivate">
                            <IconButton size="small" color="error" onClick={() => handleDeleteProduct(product)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Product Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Product</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {createError && (
              <Alert severity="error" onClose={() => setCreateError(null)}>
                {createError}
              </Alert>
            )}
            <TextField
              label="Lookup Key"
              value={newProduct.lookupKey}
              onChange={(e) => setNewProduct({ ...newProduct, lookupKey: e.target.value })}
              helperText="Must start with 'squareup_', e.g., squareup_credit_pack_5"
              fullWidth
              required
            />
            <TextField
              label="Name"
              value={newProduct.name}
              onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newProduct.description}
              onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={newProduct.type}
                label="Type"
                onChange={(e) => setNewProduct({ ...newProduct, type: e.target.value as ProductType })}
              >
                <MenuItem value="credit_pack">Credit Pack</MenuItem>
                <MenuItem value="bundle">Bundle</MenuItem>
                <MenuItem value="feature">Feature</MenuItem>
                <MenuItem value="subscription">Subscription</MenuItem>
              </Select>
            </FormControl>

            {newProduct.type === 'credit_pack' && (
              <TextField
                label="Board Credits"
                type="number"
                value={newProduct.metadata.boardCredits || ''}
                onChange={(e) => setNewProduct({
                  ...newProduct,
                  metadata: { ...newProduct.metadata, boardCredits: parseInt(e.target.value) || undefined }
                })}
                helperText="Number of board creation credits to grant"
                fullWidth
              />
            )}

            {newProduct.type === 'bundle' && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Bundle Items</Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Add Product</InputLabel>
                  <Select
                    value=""
                    label="Add Product"
                    onChange={(e) => addBundleItem(e.target.value as string)}
                  >
                    {availableForBundle
                      .filter(p => !selectedBundleItems.find(item => item.productId === p.id))
                      .map(p => (
                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                      ))
                    }
                  </Select>
                </FormControl>
                <List dense>
                  {selectedBundleItems.map(item => {
                    const product = availableForBundle.find(p => p.id === item.productId);
                    return (
                      <ListItem key={item.productId}>
                        <ListItemText primary={product?.name || item.productId} />
                        <ListItemSecondaryAction>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity}
                            onChange={(e) => updateBundleItemQuantity(item.productId, parseInt(e.target.value) || 1)}
                            sx={{ width: 60, mr: 1 }}
                            inputProps={{ min: 1 }}
                          />
                          <IconButton edge="end" size="small" onClick={() => removeBundleItem(item.productId)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </>
            )}

            <TextField
              label="Display Order"
              type="number"
              value={newProduct.displayOrder}
              onChange={(e) => setNewProduct({ ...newProduct, displayOrder: parseInt(e.target.value) || 0 })}
              helperText="Lower numbers appear first"
              fullWidth
            />

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2">Pricing</Typography>

            <TextField
              label="Price (cents)"
              type="number"
              value={newProductPrice.amount || ''}
              onChange={(e) => setNewProductPrice({ ...newProductPrice, amount: parseInt(e.target.value) || 0 })}
              helperText={newProductPrice.amount > 0 ? `Display: ${formatPrice(newProductPrice.amount)}` : 'Leave empty to add price later'}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={newProductPrice.currency}
                label="Currency"
                onChange={(e) => setNewProductPrice({ ...newProductPrice, currency: e.target.value })}
              >
                <MenuItem value="usd">USD</MenuItem>
                <MenuItem value="eur">EUR</MenuItem>
                <MenuItem value="gbp">GBP</MenuItem>
              </Select>
            </FormControl>

            {newProduct.type === 'subscription' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Billing Interval</InputLabel>
                  <Select
                    value={newProductPrice.interval || ''}
                    label="Billing Interval"
                    onChange={(e) => setNewProductPrice({
                      ...newProductPrice,
                      interval: e.target.value as 'month' | 'year' | undefined || undefined
                    })}
                  >
                    <MenuItem value="">One-time</MenuItem>
                    <MenuItem value="month">Monthly</MenuItem>
                    <MenuItem value="year">Yearly</MenuItem>
                  </Select>
                </FormControl>
                {newProductPrice.interval && (
                  <TextField
                    label="Interval Count"
                    type="number"
                    value={newProductPrice.intervalCount || 1}
                    onChange={(e) => setNewProductPrice({ ...newProductPrice, intervalCount: parseInt(e.target.value) || 1 })}
                    helperText={`Bill every ${newProductPrice.intervalCount || 1} ${newProductPrice.interval}(s)`}
                    fullWidth
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateProduct}
            variant="contained"
            disabled={createLoading || !newProduct.lookupKey || !newProduct.name}
          >
            {createLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {editError && (
              <Alert severity="error" onClose={() => setEditError(null)}>
                {editError}
              </Alert>
            )}
            <TextField
              label="Name"
              value={editInput.name || ''}
              onChange={(e) => setEditInput({ ...editInput, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={editInput.description || ''}
              onChange={(e) => setEditInput({ ...editInput, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            {editProduct?.type === 'credit_pack' && (
              <TextField
                label="Board Credits"
                type="number"
                value={editInput.metadata?.boardCredits || ''}
                onChange={(e) => setEditInput({
                  ...editInput,
                  metadata: { ...editInput.metadata, boardCredits: parseInt(e.target.value) || undefined }
                })}
                fullWidth
              />
            )}
            <TextField
              label="Display Order"
              type="number"
              value={editInput.displayOrder ?? editProduct?.displayOrder ?? 0}
              onChange={(e) => setEditInput({ ...editInput, displayOrder: parseInt(e.target.value) || 0 })}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editInput.active ?? editProduct?.active ?? true}
                  onChange={(e) => setEditInput({ ...editInput, active: e.target.checked })}
                />
              }
              label="Active"
            />

            {editProduct?.prices && editProduct.prices.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Prices</Typography>
                <List dense>
                  {editProduct.prices.map(price => (
                    <ListItem key={price.id}>
                      <ListItemText
                        primary={formatPrice(price.amount)}
                        secondary={price.interval ? `per ${price.interval}` : 'one-time'}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          size="small"
                          label={price.active ? 'Active' : 'Inactive'}
                          color={price.active ? 'success' : 'default'}
                        />
                        {price.active && (
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleDeactivatePrice(price.id)}
                            sx={{ ml: 1 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateProduct}
            variant="contained"
            disabled={editLoading}
          >
            {editLoading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Price Dialog */}
      <Dialog open={priceDialogOpen} onClose={() => setPriceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Price for {priceProduct?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {priceError && (
              <Alert severity="error" onClose={() => setPriceError(null)}>
                {priceError}
              </Alert>
            )}
            <TextField
              label="Amount (cents)"
              type="number"
              value={newPrice.amount}
              onChange={(e) => setNewPrice({ ...newPrice, amount: parseInt(e.target.value) || 0 })}
              helperText={`Display: ${formatPrice(newPrice.amount || 0)}`}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select
                value={newPrice.currency}
                label="Currency"
                onChange={(e) => setNewPrice({ ...newPrice, currency: e.target.value })}
              >
                <MenuItem value="usd">USD</MenuItem>
                <MenuItem value="eur">EUR</MenuItem>
                <MenuItem value="gbp">GBP</MenuItem>
              </Select>
            </FormControl>
            {priceProduct?.type === 'subscription' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Billing Interval</InputLabel>
                  <Select
                    value={newPrice.interval || ''}
                    label="Billing Interval"
                    onChange={(e) => setNewPrice({ ...newPrice, interval: e.target.value as 'month' | 'year' | undefined })}
                  >
                    <MenuItem value="">One-time</MenuItem>
                    <MenuItem value="month">Monthly</MenuItem>
                    <MenuItem value="year">Yearly</MenuItem>
                  </Select>
                </FormControl>
                {newPrice.interval && (
                  <TextField
                    label="Interval Count"
                    type="number"
                    value={newPrice.intervalCount || 1}
                    onChange={(e) => setNewPrice({ ...newPrice, intervalCount: parseInt(e.target.value) || 1 })}
                    helperText={`Bill every ${newPrice.intervalCount || 1} ${newPrice.interval}(s)`}
                    fullWidth
                  />
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreatePrice}
            variant="contained"
            disabled={priceLoading || !newPrice.amount}
          >
            {priceLoading ? <CircularProgress size={24} /> : 'Add Price'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
